import {offerAlternativeGeneratedApi} from './codegen/__generated__/api.generated';

const OFFER_ALTERNATIVES_TAG = 'OfferAlternatives' as const;
/** The vehicle → Business Case reverse link of stock alternatives. */
const VEHICLE_OFFER_ALTERNATIVES_TAG = 'VehicleOfferAlternatives' as const;

// Owned by @dms/api/new-vehicle on the same base API; registered here too so the types know them.
const NEW_VEHICLE_CONFIGURATION_TAG = 'NewVehicleConfiguration' as const;
const NEW_VEHICLE_ORDER_TAG = 'NewVehicleOrder' as const;

const listTag = (businessCaseId: string) => ({type: OFFER_ALTERNATIVES_TAG, id: businessCaseId});

/**
 * Same set as switching the sale vehicle: applying an alternative (or seeding the deal vehicle as
 * alternative A on the first add) changes the deal vehicle checkout and prices read.
 */
const dealVehicleTags = (businessCaseId: string) => [
  'Checkout' as const,
  'SaleVehicle' as const,
  'Vehicle' as const,
  // Applying a configuration materializes its vehicle and order.
  NEW_VEHICLE_CONFIGURATION_TAG,
  NEW_VEHICLE_ORDER_TAG,
  'offer' as const,
  'BusinessCaseSalePrices' as const,
  'SaleVehicleActions' as const,
  {type: 'BusinessCaseDetail' as const, id: businessCaseId},
  {type: 'BusinessCaseActions' as const, id: businessCaseId},
];

// Mutations also invalidate on error: a 409/404 means the list moved and must be refetched.
export const offerAlternativeApi = offerAlternativeGeneratedApi.enhanceEndpoints({
  addTagTypes: [
    OFFER_ALTERNATIVES_TAG,
    VEHICLE_OFFER_ALTERNATIVES_TAG,
    NEW_VEHICLE_CONFIGURATION_TAG,
    NEW_VEHICLE_ORDER_TAG,
  ],
  endpoints: {
    getOfferAlternatives: {
      // Also tied to the Business Case detail: `isApplied` and the applied terms are derived from
      // the deal vehicle, which the legacy sale-vehicle endpoints change (remove, price, discount).
      providesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        {type: 'BusinessCaseDetail' as const, id: businessCaseId},
        // Configurator save/reprice changes SOURCE_CHANGED, selectable and choice currency.
        NEW_VEHICLE_CONFIGURATION_TAG,
      ],
    },
    getVehicleOfferAlternatives: {
      // Legacy removal or closure of a listed Business Case invalidates its detail.
      providesTags: (result) => [
        VEHICLE_OFFER_ALTERNATIVES_TAG,
        ...(result?.items ?? []).map(({businessCaseId}) => ({
          type: 'BusinessCaseDetail' as const,
          id: businessCaseId,
        })),
      ],
    },
    addOfferAlternative: {
      invalidatesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        VEHICLE_OFFER_ALTERNATIVES_TAG,
        ...dealVehicleTags(businessCaseId),
      ],
    },
    updateOfferAlternative: {
      invalidatesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        VEHICLE_OFFER_ALTERNATIVES_TAG,
      ],
    },
    addOfferAlternativeUpsellFromCatalog: {
      invalidatesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        VEHICLE_OFFER_ALTERNATIVES_TAG,
      ],
    },
    withdrawOfferAlternative: {
      invalidatesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        VEHICLE_OFFER_ALTERNATIVES_TAG,
      ],
    },
    applyOfferAlternative: {
      invalidatesTags: (_result, _error, {businessCaseId}) => [
        listTag(businessCaseId),
        VEHICLE_OFFER_ALTERNATIVES_TAG,
        ...dealVehicleTags(businessCaseId),
      ],
    },
  },
});

export const {
  useGetOfferAlternativesQuery,
  useAddOfferAlternativeMutation,
  useUpdateOfferAlternativeMutation,
  useAddOfferAlternativeUpsellFromCatalogMutation,
  useWithdrawOfferAlternativeMutation,
  useApplyOfferAlternativeMutation,
  useGetVehicleOfferAlternativesQuery,
} = offerAlternativeApi;
